import React, { useState } from 'react';
import '../InvoiceDetail.css';

/**
 * MultiIbanField - Component for managing multiple IBAN bank accounts
 *
 * Props:
 *   - ibans: Array of IBAN strings (can also be string for backward compatibility)
 *   - onChange: Callback when IBANs change
 *   - label: Label for the field
 *   - readOnly: If true, displays IBANs without edit capability
 */
export default function MultiIbanField({ ibans, onChange, label = "IBAN", readOnly = false }) {
  const [newIban, setNewIban] = useState('');
  const [error, setError] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  // Normalize ibans to always be an array
  const normalizeIbans = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      // Handle comma-separated or single IBAN
      return value.includes(',')
        ? value.split(',').map(i => i.trim()).filter(i => i)
        : [value];
    }
    return [];
  };

  const ibanList = normalizeIbans(ibans);

  // Basic IBAN validation (simple check for Romanian IBANs)
  // Format: RO + 2 check digits + up to 30 alphanumeric characters
  const isValidIban = (iban) => {
    // Remove spaces and convert to uppercase
    const cleanIban = iban.replace(/\s/g, '').toUpperCase();

    // Basic validation: starts with country code (2 letters) + 2 digits + alphanumeric
    // Romanian IBAN: RO + 2 digits + 4 letters + 16 digits = 24 characters
    const ibanRegex = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{1,30}$/;

    if (!ibanRegex.test(cleanIban)) {
      return false;
    }

    // Must be between 15-34 characters (international standard)
    if (cleanIban.length < 15 || cleanIban.length > 34) {
      return false;
    }

    return true;
  };

  // Add new IBAN
  const handleAddIban = () => {
    const trimmedIban = newIban.trim().toUpperCase().replace(/\s/g, '');

    if (!trimmedIban) {
      setError('Introduceți un IBAN');
      return;
    }

    if (!isValidIban(trimmedIban)) {
      setError('Format IBAN invalid (ex: RO49AAAA1B31007593840000)');
      return;
    }

    if (ibanList.includes(trimmedIban)) {
      setError('Acest IBAN există deja');
      return;
    }

    const updatedIbans = [...ibanList, trimmedIban];
    onChange(updatedIbans);
    setNewIban('');
    setError('');
    setIsAdding(false);
  };

  // Cancel adding
  const handleCancelAdd = () => {
    setNewIban('');
    setError('');
    setIsAdding(false);
  };

  // Remove IBAN
  const handleRemoveIban = (index) => {
    const updatedIbans = ibanList.filter((_, i) => i !== index);
    onChange(updatedIbans.length > 0 ? updatedIbans : null);
  };

  // Handle Enter key
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddIban();
    }
  };

  if (readOnly) {
    // Read-only display
    return (
      <div className="form-field">
        <div className="form-field-label">{label}</div>
        <div className="form-field-value">
          {ibanList.length > 0 ? (
            <div className="email-list-readonly">
              {ibanList.map((iban, index) => (
                <span key={index} className="email-tag-readonly">
                  {iban}
                </span>
              ))}
            </div>
          ) : (
            '—'
          )}
        </div>
      </div>
    );
  }

  // Editable mode
  return (
    <div className="form-field">
      <div className="form-field-label">{label}</div>
      <div className="email-field-container">
        {/* Existing IBANs display */}
        <div className="email-display-box">
          {ibanList.length > 0 ? (
            <div className="email-list">
              {ibanList.map((iban, index) => (
                <span key={index} className="email-tag">
                  <span className="email-tag-text">{iban}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveIban(index)}
                    className="email-tag-remove"
                    title="Șterge IBAN"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <span className="email-placeholder">—</span>
          )}

          {/* Add button */}
          {!isAdding && (
            <button
              type="button"
              onClick={() => setIsAdding(true)}
              className="email-add-trigger"
              title="Adaugă IBAN"
            >
              +
            </button>
          )}
        </div>

        {/* Add IBAN input (shown when isAdding is true) */}
        {isAdding && (
          <div className="email-input-section">
            <div className="email-input-group">
              <input
                type="text"
                className="clients-input"
                placeholder="RO49AAAA1B31007593840000"
                value={newIban}
                onChange={(e) => {
                  setNewIban(e.target.value);
                  setError('');
                }}
                onKeyPress={handleKeyPress}
                autoFocus
              />
              <button
                type="button"
                onClick={handleAddIban}
                className="email-action-btn email-action-btn-add"
              >
                Adaugă
              </button>
              <button
                type="button"
                onClick={handleCancelAdd}
                className="email-action-btn email-action-btn-cancel"
              >
                Anulează
              </button>
            </div>
            {error && (
              <p className="email-error">{error}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
